class ProductHeroGallery extends HTMLElement {
    #page = 0;
    #mediaQuery = window.matchMedia('(min-width: 750px)');

    connectedCallback() {
      this.track = this.querySelector('[data-hero-track]');
      this.allItems = Array.from(this.querySelectorAll('[data-hero-main-item]'));
      this.mainItems = this.allItems;
      this.prevButton = this.querySelector('[data-hero-prev]');
      this.nextButton = this.querySelector('[data-hero-next]');

      this.prevButton?.addEventListener('click', () => this.paginate(-1));
      this.nextButton?.addEventListener('click', () => this.paginate(1));
      this.#mediaQuery.addEventListener('change', () => {
        this.#page = 0;
        this.#render();
      });

      this.#handleVariantChange = this.#handleVariantChange.bind(this);
      this.#handleSwatchClick = this.#handleSwatchClick.bind(this);
      document.addEventListener('change', this.#handleVariantChange);
      // Fallback for mobile: 'change' may be stopped by stopPropagation in SwatchesVariantPickerComponent.
      // 'click' on labels/inputs is not stopped and fires reliably on touch devices.
      document.addEventListener('click', this.#handleSwatchClick);

      const initialColorInput = document.querySelector('input[name^="Color" i]:checked, select[name^="Color" i]');
      const initialColor = this.#colorFromInput(initialColorInput);
      if (initialColor) {
        this.#filterByColor(initialColor);
      } else {
        this.#render();
      }
    }

    disconnectedCallback() {
      document.removeEventListener('change', this.#handleVariantChange);
      document.removeEventListener('click', this.#handleSwatchClick);
    }

    #colorFromInput(input) {
      if (!input) return null;
      const raw =
        input.tagName === 'SELECT'
          ? input.options[input.selectedIndex]?.value
          : input.getAttribute('aria-label') || input.value;
      return raw ? raw.trim().toLowerCase() : null;
    }

    #handleVariantChange(event) {
      const input = event.target;
      if (!input.matches('input[type="radio"], select')) return;
      if (!input.name || !input.name.toLowerCase().startsWith('color')) return;

      const colorValue = this.#colorFromInput(input);
      if (colorValue) this.#filterByColor(colorValue);
    }

    // Catches swatch label clicks even when the 'change' event is stopped by stopPropagation.
    #handleSwatchClick(event) {
      const label = event.target instanceof HTMLLabelElement
        ? event.target
        : event.target.closest?.('label');
      if (!label) return;
      const input = label.querySelector('input[type="radio"]');
      if (!input) return;
      if (!input.name?.toLowerCase().startsWith('color')) return;
      // Use rAF so the browser has checked the radio before we read its state.
      requestAnimationFrame(() => {
        if (!input.checked) return;
        const colorValue = this.#colorFromInput(input);
        if (colorValue) this.#filterByColor(colorValue);
      });
    }

    #filterByColor(colorValue) {
      const matched = this.allItems.filter((item) => {
        const alt = item.getAttribute('data-media-alt') || '';
        return alt.includes(colorValue);
      });

      this.mainItems = matched.length > 0 ? matched : this.allItems;

      this.allItems.forEach((item) => {
        item.style.display = this.mainItems.includes(item) ? '' : 'none';
      });

      this.#page = 0;
      this.#render();
    }

    get #itemsPerPage() {
      return this.#mediaQuery.matches ? 3 : 1;
    }

    paginate(direction) {
      const maxPage = Math.ceil(this.mainItems.length / this.#itemsPerPage) - 1;
      this.#page = Math.min(Math.max(this.#page + direction, 0), maxPage);
      this.#render();
    }

    #render() {
      const itemsPerPage = this.#itemsPerPage;

      if (this.track) {
        this.track.style.transform = `translateX(${this.#page * -100}%)`;
      }

      this.mainItems.forEach((item, index) => {
        const page = Math.floor(index / itemsPerPage);
        item.setAttribute('aria-hidden', String(page !== this.#page));
      });

      const maxPage = Math.ceil(this.mainItems.length / itemsPerPage) - 1;
      if (this.prevButton) this.prevButton.disabled = this.#page <= 0;
      if (this.nextButton) this.nextButton.disabled = this.#page >= maxPage;
    }
  }

  if (!customElements.get('product-hero-gallery')) {
    customElements.define('product-hero-gallery', ProductHeroGallery);
  }
